import { useEffect, useRef, useState } from 'react'
import { getContentByTopic, getContentJournals, getContentSurveys, getPrograms } from '../../server/server'

/**
 * Data source for the ready-to-go library.
 *
 * One hook instance per source (type list or topic list). Results are kept in a
 * module-level cache keyed by `${kind}:${param}:${locale}` with
 * stale-while-revalidate semantics: a cache hit renders immediately with
 * `loading=false` and a refetch runs in the background; a miss renders
 * `loading=true` with `data=null` until the first response arrives. A refetch
 * that returns identical data does not touch state, so nothing re-renders or
 * replays entrance animations.
 *
 * Each hook has its own request counter, so the type and topic sources can
 * never cancel each other's requests (the bug that let survey rows show up
 * under the Programs tab).
 */

const EMPTY_LIST = Object.freeze([])
const EMPTY_TOPIC = Object.freeze({ programs: EMPTY_LIST, surveys: EMPTY_LIST, journals: EMPTY_LIST })

const EMPTY_BY_KIND = {
    Programs: EMPTY_LIST,
    Surveys: EMPTY_LIST,
    Journals: EMPTY_LIST,
    topic: EMPTY_TOPIC,
}

const assertOk = (resp) => {
    if (resp?.status !== 200) throw new Error(`Unexpected status ${resp?.status}`)
    return resp.data
}

const FETCHERS = {
    Programs: async (_param, locale) => {
        const data = assertOk(await getPrograms(locale))
        return (data?.list || []).filter((item) => item?.days?.length !== 0)
    },
    Surveys: async (_param, locale) => {
        const data = assertOk(await getContentSurveys())
        return (data || []).filter((e) => e?.locale === locale)
    },
    Journals: async (_param, locale) => {
        const data = assertOk(await getContentJournals())
        return (data || []).filter((e) => e?.locale === locale)
    },
    topic: async (topic, locale) => {
        const data = assertOk(await getContentByTopic(topic, locale)) || {}
        return {
            programs: Array.isArray(data.programs) ? data.programs : EMPTY_LIST,
            surveys: Array.isArray(data.surveys) ? data.surveys : EMPTY_LIST,
            journals: Array.isArray(data.journals) ? data.journals : EMPTY_LIST,
        }
    },
}

const cache = new Map()

const isSameData = (a, b) => {
    if (a === b) return true
    try {
        return JSON.stringify(a) === JSON.stringify(b)
    } catch (err) {
        return false
    }
}

/** Test/debug helper: drop everything cached. Not used by the UI. */
export const clearReadyToGoCache = () => cache.clear()

export default function useReadyToGoContent({ kind, param = '', locale = 'en', enabled = true }) {
    const cacheKey = `${kind}:${param}:${locale}`
    const [snapshot, setSnapshot] = useState(() => ({ key: cacheKey, data: cache.get(cacheKey) ?? null, arrivals: 0 }))
    const requestRef = useRef(0)
    const mountedRef = useRef(true)

    useEffect(() => {
        mountedRef.current = true
        return () => {
            mountedRef.current = false
        }
    }, [])

    useEffect(() => {
        if (!enabled) return
        const fetcher = FETCHERS[kind]
        if (!fetcher) {
            console.error(`useReadyToGoContent: unknown kind "${kind}"`)
            return
        }
        const requestId = ++requestRef.current
        const isCurrent = () => mountedRef.current && requestId === requestRef.current

        fetcher(param, locale)
            .then((data) => {
                if (!isCurrent()) return
                const previous = cache.get(cacheKey)
                if (previous !== undefined && isSameData(previous, data)) return
                cache.set(cacheKey, data)
                setSnapshot((prev) => ({ key: cacheKey, data, arrivals: prev.key === cacheKey ? prev.arrivals + 1 : 1 }))
            })
            .catch((err) => {
                if (!isCurrent()) return
                console.error(`useReadyToGoContent: ${kind} load failed`, err)
                // Keep good cached data on a failed refresh; only fall back to
                // empty when there is nothing to show at all.
                if (!cache.has(cacheKey)) setSnapshot({ key: cacheKey, data: EMPTY_BY_KIND[kind] ?? EMPTY_LIST, arrivals: 1 })
            })
        // kind/param/locale are all folded into cacheKey
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cacheKey, enabled])

    // Read synchronously so a cached tab renders its data on the very first
    // frame instead of flashing the previous tab's list.
    const current = snapshot.key === cacheKey ? snapshot : { key: cacheKey, data: cache.get(cacheKey) ?? null, arrivals: 0 }
    const loading = enabled && current.data === null

    return { data: current.data, loading, version: `${cacheKey}:${current.arrivals}` }
}

/** Case-insensitive name/title match used by the search box. */
export const matchesSearch = (item, term) => {
    if (!term) return true
    return `${item?.name ?? item?.title ?? ''}`.toLowerCase().includes(term)
}

export const filterList = (list, term) => {
    const safe = Array.isArray(list) ? list : EMPTY_LIST
    if (!term) return safe
    return safe.filter((item) => matchesSearch(item, term))
}

export const filterTopicContent = (data, term) => {
    const safe = data || EMPTY_TOPIC
    if (!term) return safe
    return {
        programs: filterList(safe.programs, term),
        surveys: filterList(safe.surveys, term),
        journals: filterList(safe.journals, term),
    }
}
